import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  TimelineEvent,
  TimeLineEventType,
  TimeLineType,
  TrackingFamily,
  FacialAuthenticationStatus,
  FacialLivenessDiagnostic,
  getTimeLine,
  getTimeLineAuthentication,
  OperationStatus,
} from '@facephi/inphinite-common';
import { useTranslation } from 'react-i18next';
import {
  OperationResultReason as ResultReason,
  antifraudValidations,
} from '../state/model';

export function useTimeLine(operationId: string, family?: string) {
  const { data, loading } = useQuery(
    family === TrackingFamily.AUTHENTICATION
      ? getTimeLineAuthentication
      : getTimeLine,
    {
      variables: { parameters: { operationId, family } },
    },
  );

  const { t } = useTranslation();

  const events = useMemo(
    () =>
      data
        ? data.timeline.events
            .filter(
              (item: TimelineEvent) =>
                item.metadata.type === TimeLineType.onboarding ||
                item.metadata.type === TimeLineType.asset ||
                item.metadata.type === TimeLineType.facialAuthenticate ||
                item.metadata.type === TimeLineType.facialLiveness ||
                item.metadata.type === TimeLineType.authentication ||
                item.metadata.type === TimeLineType.operationResult ||
                item.metadata.type === TimeLineType.securityInfoData ||
                item.metadata.type === TimeLineType.wallet ||
                item.metadata.type === TimeLineType.credential ||
                item.metadata.type === TimeLineType.stepChange ||
                item.metadata.type === TimeLineType.stepResult,
            )
            .map((item: TimelineEvent) => ({
              ...item,
              metadata:
                item.metadata.type === TimeLineType.asset
                  ? { ...item.metadata, event: TimeLineEventType.asset }
                  : item.metadata,
            }))
            .filter((item: TimelineEvent) => {
              return (
                item.metadata.event === TimeLineEventType.screenAccess ||
                item.metadata.event === TimeLineEventType.onboardingResult ||
                item.metadata.event === TimeLineEventType.asset ||
                item.metadata.event === TimeLineEventType.recording ||
                item.metadata.event === TimeLineEventType.stepChange ||
                item.metadata.type === TimeLineType.operationResult ||
                item.metadata.type === TimeLineType.facialAuthenticate ||
                item.metadata.type === TimeLineType.facialLiveness ||
                item.metadata.type === TimeLineType.securityInfoData ||
                item.metadata.type === TimeLineType.wallet ||
                item.metadata.type === TimeLineType.credential ||
                item.metadata.type === TimeLineType.stepChange ||
                item.metadata.type === TimeLineType.stepResult
              );
            })
        : [],
    [data],
  );

  const getMessageTimeline = (
    value: TimeLineEventType,
    screen: string,
    data: string,
    type: string,
    reason: string,
    mode: string,
    diagnostic?: FacialLivenessDiagnostic,
    authStatus?: FacialAuthenticationStatus,
    operationStatus?: string,
    source?: string,
    walletId?: string,
    credentialType?: string,
  ) => {
    if (type === TimeLineEventType.facialAuthenticate && authStatus) {
      return t('Authentication status', { status: t(authStatus) });
    }

    if (type === TimeLineEventType.facialLiveness && diagnostic) {
      return t('Facial Liveness status', { status: t(diagnostic) });
    }

    if (type === TimeLineType.asset) {
      return t('1 new document uploaded');
    } else {
      if (value === TimeLineEventType.screenAccess) {
        return t(value, { screen: screen });
      } else if (value === TimeLineEventType.onboardingResult) {
        return t(value, { result: t(data) });
      } else if (value === TimeLineEventType.stepChange) {
        return t(value, { result: t(data) });
      } else if (type === TimeLineEventType.securityInfoData) {
        return antifraudValidations.includes(mode)
          ? t(mode as string)
          : t(type, { source: source });
      } else if (type === TimeLineType.operationResult) {
        return reason !== ResultReason.documentValidationSidesError
          ? t(reason) || t(operationStatus as string)
          : t(operationStatus as string);
      } else if (type === TimeLineType.wallet) {
        return t(type, { result: walletId });
      } else if (type === TimeLineType.credential) {
        return t(type, { result: credentialType });
      }
    }

    return t(value);
  };

  const getMessageDataTimeline = (
    value: TimeLineType,
    stepId: string,
    stepType: string,
    reason: string,
    status?: OperationStatus,
  ) => {
    if (value === TimeLineType.stepChange) {
      return t(value, { result: stepType });
    } else if (value === TimeLineType.stepResult) {
      return t(value, { step: t(stepType), result: t(status as string) });
    }
    return t(value);
  };

  return {
    events,
    eventMessage: getMessageTimeline,
    dataEventMessage: getMessageDataTimeline,
    loadingEvents: loading,
  };
}
